import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type RollNumberStrategy = "section-wise" | "whole-class"

// 2026-10-02: Term is now global (RMS-parity) -- no longer tied to one
// campus or one session. Where it applies lives in TermScope instead (see
// below). `branch`/`session` may still be present on old, not-yet-migrated
// docs -- harmless leftovers, no longer read by this page.
export type Term = {
  _id: string
  title: string
  status?: "active" | "inactive"
  type?: string | null
  rollNumberStrategy?: RollNumberStrategy | null
  displayOrder?: number
  // Marks the final exam of the cycle (informational -- report-card
  // carryover is actually driven per-subject, see CourseStructure).
  isFinal?: boolean
  createdAt: string
  updatedAt: string
}

export type TermScope = {
  _id?: string
  branch: string | { _id: string; name: string }
  systemId: string | { _id: string; name: string; code?: string }
  // Omitted/undefined means "all classes for this campus+board".
  classId?: string | { _id: string; name: string } | null
}

export type PaginationData = {
  total: number
  totalPages: number
  currentPage: number
  limit: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export type CreateTermData = {
  title: string
  type?: string | null
  rollNumberStrategy?: RollNumberStrategy | null
  displayOrder?: number
  isFinal?: boolean
}

export type UpdateTermData = Partial<CreateTermData>

export type TermsResponse = {
  success: boolean
  data: Term[]
  pagination: PaginationData
}

const TERM_ENDPOINT = {
  TERMS: "/term",
}

const getAllTerms = async (
  page = 1,
  limit = 10,
  searchText?: string,
  status?: "active" | "inactive",
): Promise<TermsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(TERM_ENDPOINT.TERMS, {
      page,
      limit,
      searchText,
      status,
    }),
    method: "GET",
  })

  return response.data
}

const getTermById = async (id: string): Promise<Term> => {
  const response = await apiClient({
    url: `${TERM_ENDPOINT.TERMS}/${id}`,
    method: "GET",
  })
  return response.data.data;
}

const createTerm = async (data: CreateTermData): Promise<Term> => {
  const response = await apiClient({
    url: TERM_ENDPOINT.TERMS,
    method: "POST",
    data,
  })
  return response.data.data;
}

const updateTerm = async (id: string, data: UpdateTermData): Promise<Term> => {
  const response = await apiClient({
    url: `${TERM_ENDPOINT.TERMS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data.data;
}

// Toggle term status (active/inactive)
const toggleTermStatus = async (
  id: string,
  status: "active" | "inactive"
): Promise<Term> => {
  const response = await apiClient({
    url: `${TERM_ENDPOINT.TERMS}/${id}/status`,
    method: "PATCH",
    data: { status },
  })
  return response.data.data;
}

// Which (campus, board, class) tuples this term applies to.
const getTermScopes = async (termId: string): Promise<TermScope[]> => {
  const response = await apiClient({
    url: `${TERM_ENDPOINT.TERMS}/${termId}/scopes`,
    method: "GET",
  })
  return response.data.data;
}

// Full replace -- same pattern as RMS's own scopes save (wipe + re-insert).
const setTermScopes = async (
  termId: string,
  scopes: { branch: string; systemId: string; classId?: string | null }[]
): Promise<TermScope[]> => {
  const response = await apiClient({
    url: `${TERM_ENDPOINT.TERMS}/${termId}/scopes`,
    method: "PUT",
    data: { scopes },
  })
  return response.data.data;
}

// Terms usable at a given campus+board(+class) -- what Result Breakdown's
// term picker needs, replacing the session/title-matching workaround.
const getTermsForScope = async (
  branchId: string,
  systemId: string,
  classId?: string
): Promise<Term[]> => {
  const response = await apiClient({
    url: appendQueryParams(`${TERM_ENDPOINT.TERMS}/for-scope`, { branchId, systemId, classId }),
    method: "GET",
  })
  return response.data.data;
}

export {
  getAllTerms,
  getTermById,
  createTerm,
  updateTerm,
  toggleTermStatus,
  getTermScopes,
  setTermScopes,
  getTermsForScope,
}
